import { TFunction, useTranslation } from 'react-i18next';
import { ServiceType } from 'src/enums/ServiceType';
import { useReport } from './useReport';
import { useReportLinksData } from 'src/hooks/common/useReportLinksData';
import { Helmet } from 'react-helmet-async';
import { PageTitle } from 'src/enums/PageTitle';
import { GoogleAdsNoSpend } from '../googleAds/NoSpend';
import { GoogleAdsPage } from 'src/components/googleAds/page/Page';
import { GridType } from 'src/enums/GridType';
import { FlexBoxColumn } from 'src/components/common/FlexBoxColumn';
import { ReportLink } from 'src/components/common/ReportLink';
import { SectionContent, GoogleShoppingTabs } from 'src/components/shopping/Grades';
import { Heading } from '../googleAds/Report';
import { MetricsTabs, MetricsAccordions } from 'src/components/shopping/metrics/Metrics';
import { GradeAccordions as GoogleShoppingAccordions } from 'src/components/common/grade/Accordions';
import { GoogleShoppingScoreSection } from 'src/enums/google/ScoreSection';
import { useMediaQuery } from '@mui/material';
import { OrionTheme } from 'src/components/theme/orion/Theme';
import BadgeFooter from 'src/components/common/BadgeFooter';
import GooglePartnerSVG from 'src/assets/GooglePartner.svg';

interface ContentProps {
  t: TFunction;
}
const Content = ({ t }: ContentProps): JSX.Element => {
  const reportlinksData = useReportLinksData();
  const isMobile = useMediaQuery(OrionTheme.breakpoints.down('sm'));

  return isMobile ? (
    <>
      <GoogleShoppingAccordions
        defaultExpandedSection={GoogleShoppingScoreSection.WASTED_SPEND}
        scoreSections={Object.values(GoogleShoppingScoreSection)}
        renderSectionContent={(sectionName) => (
          <SectionContent sectionName={sectionName as GoogleShoppingScoreSection} isMobile={true} />
        )}
      />
      <Heading whiteSpace="pre" text={t('metrics.deepDive')} isMobile={isMobile} />
      <MetricsAccordions />
      <Heading whiteSpace="pre" text={t('resultsByProduct')} isMobile={isMobile} />
      <FlexBoxColumn sx={{ gap: 2 }}>
        {reportlinksData.map((reportLinkData) => (
          <ReportLink
            adType={reportLinkData.adtype}
            url={reportLinkData.url}
            isMobile={isMobile}
            key={reportLinkData.pathName}
            scorename={reportLinkData.scoreName}
            scorevalue={reportLinkData.scoreValue}
          />
        ))}
      </FlexBoxColumn>
    </>
  ) : (
    <>
      <GoogleShoppingTabs />
      <Heading whiteSpace="pre" text={t('resultsByProduct')} isMobile={isMobile} />
      <MetricsTabs />
      <Heading whiteSpace="normal" text={t('resultsByProduct')} isMobile={isMobile} />
      <FlexBoxColumn sx={{ gap: 2 }}>
        {reportlinksData.map((reportLinkData) => (
          <ReportLink
            adType={reportLinkData.adtype}
            url={reportLinkData.url}
            isMobile={isMobile}
            key={reportLinkData.pathName}
            scorename={reportLinkData.scoreName}
            scorevalue={reportLinkData.scoreValue}
          />
        ))}
      </FlexBoxColumn>
    </>
  );
};

export const ShoppingReport = (): JSX.Element => {
  const { t } = useTranslation([ServiceType.GOOGLE]);
  const { isZeroDollarsSpent } = useReport();

  return (
    <>
      <Helmet>
        <title>
          {PageTitle.SHOPPING} | {'Report'}
        </title>
      </Helmet>
      {isZeroDollarsSpent && <GoogleAdsNoSpend />}
      {!isZeroDollarsSpent && (
        <GoogleAdsPage
          gridType={GridType.TAB}
          additionalFooter={
            <BadgeFooter
              i18nNameSpace={ServiceType.GOOGLE}
              badgeSVG={GooglePartnerSVG}
              iconHref={t('googleBadgeLink')}
            />
          }
        >
          <Content t={t} />
        </GoogleAdsPage>
      )}
    </>
  );
};
